"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { motion, useScroll, useTransform } from "framer-motion";
import { MobileMenu } from "./MobileMenu";
import { useI18n } from "@/i18n/I18nProvider";

const navLinks = [
  { href: "/", key: "home" as const },
  { href: "/about/", key: "about" as const },
  { href: "/projects/", key: "projects" as const },
  { href: "/blog/", key: "blog" as const },
  { href: "/cv", key: "cv" as const },
  { href: "/contact/", key: "contact" as const },
];

export function Header() {
  const { t, locale, setLocale } = useI18n();
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { scrollY } = useScroll();

  const bgOpacity = useTransform(scrollY, [0, 100], [0.4, 0.9]);
  const blur = useTransform(scrollY, [0, 100], [8, 20]);
  const borderOpacity = useTransform(scrollY, [0, 100], [0.05, 0.15]);

  // Close menu on pathname change
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  return (
    <>
      <motion.header
        className="sticky top-0 z-50 px-4 pt-4"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.2, 0, 0, 1] }}
      >
        <motion.nav
          className="mx-auto flex max-w-6xl items-center justify-between rounded-2xl px-6 py-4"
          style={{
            backgroundColor: useTransform(bgOpacity, (v) => `rgba(10, 15, 28, ${v})`),
            backdropFilter: useTransform(blur, (v) => `blur(${v}px)`),
            WebkitBackdropFilter: useTransform(blur, (v) => `blur(${v}px)`),
            borderWidth: 1,
            borderStyle: "solid",
            borderColor: useTransform(borderOpacity, (v) => `rgba(59, 130, 246, ${v})`),
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          }}
        >
          {/* Logo (go to admin) */}
          <Link
            href="/admin/"
            className="group relative font-mono text-xl font-bold"
          >
            <span className="text-gradient">TVH</span>
            <span className="text-dev-text-muted">.dev</span>
            <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-gradient-to-r from-dev-blue to-dev-cyan transition-all duration-300 group-hover:w-full" />
          </Link>

          {/* Desktop Nav + Language switcher */}
          <div className="hidden items-center gap-3 md:flex">
            <ul className="flex items-center gap-1">
              {navLinks.map((link) => {
                const isActive =
                  link.href === "/"
                    ? pathname === "/"
                    : pathname.startsWith(link.href.replace(/\/$/, ""));
                const label = t(`nav.${link.key}`);
                return (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className={`group relative rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 ${isActive
                          ? "text-dev-cyan"
                          : "text-dev-text-secondary hover:text-white"
                        }`}
                    >
                      {isActive && (
                        <motion.span
                          layoutId="nav-active"
                          className="absolute inset-0 rounded-lg bg-dev-blue/10 border border-dev-blue/20"
                          transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        />
                      )}
                      <span className="relative z-10">{label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>

            <button
              type="button"
              onClick={() => setLocale(locale === "vi" ? "en" : "vi")}
              className="inline-flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 px-3 py-1 text-xs font-mono uppercase tracking-wide text-dev-text-secondary transition-colors hover:border-dev-cyan/60 hover:text-white"
            >
              <span>{locale === "vi" ? "VI" : "EN"}</span>
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(true)}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 text-dev-text-muted transition-colors hover:bg-white/10 hover:text-white md:hidden"
            aria-label="Open menu"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>
        </motion.nav>
      </motion.header>

      <MobileMenu
        isOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        links={navLinks.map((link) => ({
          href: link.href,
          label: t(`nav.${link.key}`),
        }))}
      />
    </>
  );
}
